import {
  HubModule,
  HubModuleCTA,
  HubModuleSiteLink,
  PropsWithTestId,
  TitleSizeType,
  UploadedImage,
} from '../../../../../../types';
import {
  DiscoveryContentTypes,
  HubModuleTypes,
} from '../../../../../../constants';
import { CallToAction } from '../../../CallToActionGroup';
import { HubModuleWrapper } from '../../HubModuleWrapper';
import { ModuleHeader } from '../../components';
import { RelatedSiteCard } from './RelatedSiteCard';
import ExternalSiteCard, { ExternalSite } from './ExternalSiteCard';

import styles from './RelatedSitesModule.module.scss';

export interface Sponsor {
  photo: string;
  path: string;
  name: string;
}

export interface Date {
  start?: string;
  end?: string;
  formatted?: string;
}

export interface Location {
  name: string;
  city: string;
  address: string;
  country: string;
  searchCity: string;
}

export interface Partner {
  id: string;
  logo?: UploadedImage;
  title: string;
  type: string[];
}

export interface HubRelatedSite {
  name: string;
  path: string;
  imageBackground: string;
  tenant: string;
  domain: string;
  topics: string[];
  startDate: string;
  endDate: string;
  formattedDate: string;
  deliveryType: string;
  sponsors: Sponsor[];
  localisedDescription: string;
  brand: {
    colours: {
      primary: string;
      secondary: string;
      accent: string;
    };
  };
  link: HubModuleSiteLink;
  additionalDeliveryTypes: string[];
  timeAsText: string;
  dates: Date[];
  locations: Location[];
  languages: string[];
  partners: Partner[];
  sponsorLogo1: UploadedImage;
  sponsorLogo2: UploadedImage;
  contentType: DiscoveryContentTypes.EVENT | DiscoveryContentTypes.COURSE;
}

export interface RelatedSitesModuleType extends HubModule {
  '@type': HubModuleTypes.RelatedSites;
  title: string;
  titleSize: TitleSizeType;
  link: HubModuleCTA;
  relatedContents: Array<HubRelatedSite | ExternalSite>;
}

export interface RelatedSitesModuleProps {
  module: RelatedSitesModuleType;
}

export default function RelatedSitesModule({
  module: {
    '@type': type,
    title,
    titleSize,
    elementId,
    visualStyle,
    relatedContents,
    link,
  },
  testId,
}: PropsWithTestId<RelatedSitesModuleProps>) {
  return (
    <HubModuleWrapper
      type={type}
      theme={visualStyle}
      elementId={elementId}
      testId={testId}
    >
      <div className="container">
        <div className="row">
          <div className="col-xs-12">
            <ModuleHeader title={title} titleSize={titleSize} />
          </div>
        </div>
        <div className={styles.relatedContentsMargin}>
          <div className="row">
            {relatedContents.map((data) => (
              <div
                className={`col-xs-12 col-md-3 ${styles.wrapperEventCard}`}
                key={data.name}
              >
                {data.contentType === DiscoveryContentTypes.EXTERNAL_SITE ? (
                  <ExternalSiteCard data={data} />
                ) : (
                  <RelatedSiteCard
                    data={data}
                    data-testid="related-site-card"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12">
            {link?.type && (
              <CallToAction
                link={link}
                variant="contained"
                align="center"
                className={styles.ctaButton}
                data-testid="related-sites-module-link"
                ga4={{
                  module_type: type,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </HubModuleWrapper>
  );
}

RelatedSitesModule.defaultProps = {
  testId: 'hub-related-sites-module',
};
